import {FormControl, FormGroup} from '@angular/forms';

import {BehaviorSubject} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';

import {
    ApiError,
    ApiResponse,
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    UsersService,
    AuthApiService,
    OperatorsService,
    UserReportInterface,
    UserWithRolesInterface,
    OperatorResponseInterface,
    RoleModel,
    OperatorModel,
    UserFilterModel,
    UserWithRolesModel,
    OperatorInputModel,
    OperatorStatusTriggerModel,
    UserSendResetPasswordInputModel
} from '@spa/api';

import {ArrayEveryValidator} from '@spa/validators';

import {Helper} from '../../../shared/classes';
import {PermissionRuleInterface} from '../../../shared/interfaces';
import {SelectControl, BaseControl} from '../../../shared/classes/controls';

import {OperatorsFormErrorStatesEnum} from '../enums';

import {
    PanelSettingsOperatorsRolesSelector,
    PanelSettingsOperatorsFormControls,
    PanelSettingsOperatorsNameMinLength,
    PanelSettingsOperatorsEmailSelector,
    PanelSettingsOperatorsStatusSelector,
    PanelSettingsOperatorsLastNameSelector,
    PanelSettingsOperatorsFirstNameSelector,
    PanelSettingsOperatorsControlsInterface,
    PanelSettingsOperatorsMiddleNameSelector,
} from '../../../../assets/descriptors';

import {environment} from '../../../../environments/environment';

export class OperatorsForm {
    private operator: OperatorModel;
    private statusTriggers: number[];

    private readonly userWithRoles: UserWithRolesModel;
    private readonly authApiService: AuthApiService;
    private readonly usersService: UsersService;
    private readonly operatorsService: OperatorsService;
    private readonly getRolePermissionRule: PermissionRuleInterface;

    public form: FormGroup;
    public controls: PanelSettingsOperatorsControlsInterface;
    public controlsObservable: BehaviorSubject<BaseControl[]>;

    constructor(
        usersService: UsersService,
        operatorsService: OperatorsService,
        operator: OperatorModel = null,
        userWithRoles: UserWithRolesModel = null,
        authApiService: AuthApiService = null,
        getRoleDetailsPermissionRule: PermissionRuleInterface = null
    ) {
        this.usersService = usersService;
        this.operatorsService = operatorsService;
        this.operator = operator;
        this.userWithRoles = userWithRoles;
        this.authApiService = authApiService;
        this.getRolePermissionRule = getRoleDetailsPermissionRule;

        this.controlsObservable = new BehaviorSubject([]);
    }

    private initControl(control: BaseControl, roles: RoleModel[]): BaseControl {
        switch (control.name) {
            case PanelSettingsOperatorsFirstNameSelector:
                control.value = !this.operator ? null : this.operator.firstName;
                break;
            case PanelSettingsOperatorsMiddleNameSelector:
                control.value = !this.operator ? null : this.operator.middleName;
                break;
            case PanelSettingsOperatorsLastNameSelector:
                control.value = !this.operator ? null : this.operator.lastName;
                break;
            case PanelSettingsOperatorsEmailSelector:
                control.value = !this.operator ? null : this.operator.email;

                if (control.value) {
                    control.disabled = true;
                }
                break;
            case PanelSettingsOperatorsStatusSelector:
                if (this.operator && this.operator.status) {
                    const currentStatusTrigger = Helper.enumsHelper().getOperatorStatusTriggerByStatus(this.operator.status);

                    const statusTriggers = (this.statusTriggers.indexOf(currentStatusTrigger) >= 0) ? this.statusTriggers :
                        this.statusTriggers.concat(currentStatusTrigger);

                    (control as SelectControl).options = statusTriggers.map((statusTrigger: number) => {
                        return {
                            label: Helper.textsHelper().getOperatorStatusNameByTrigger(statusTrigger),
                            value: statusTrigger
                        };
                    });

                    control.value = currentStatusTrigger;
                }
                break;
            case PanelSettingsOperatorsRolesSelector:
                const roleIds = [];

                (control as SelectControl).value = !this.userWithRoles ? null :
                    this.userWithRoles.getRoleIds();

                (control as SelectControl).options = roles.map((role: RoleModel) => {
                    roleIds.push(role.id);

                    return {label: role.name, value: role.id};
                });

                control.extendValidators(ArrayEveryValidator(roleIds));
                break;
        }

        return control;
    }

    private registerOperator(): ServerResponse<boolean> {
        const email = this.form.value[PanelSettingsOperatorsEmailSelector];
        const filter = new UserFilterModel(email, environment.authId);

        return this.usersService.search(filter).pipe(
            switchMap((searchResponse: ServerResponseData<UserReportInterface>) => {
                if (searchResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponseObservable<UserReportInterface>(
                        searchResponse,
                        OperatorsFormErrorStatesEnum.GetUserFailed
                    );
                }

                if (searchResponse.data) {
                    const error = new ApiError(OperatorsFormErrorStatesEnum.UserExists);
                    const errorResponse = new ApiResponse(HttpStatusCodesEnum.BadRequest, [error]);

                    return Helper.errorsHelper().createErrorResponseObservable<UserReportInterface>(errorResponse);
                }

                const inputModel = new OperatorInputModel(
                    this.form.value[PanelSettingsOperatorsFirstNameSelector],
                    this.form.value[PanelSettingsOperatorsMiddleNameSelector],
                    this.form.value[PanelSettingsOperatorsLastNameSelector],
                    null,
                    email,
                    this.form.value[PanelSettingsOperatorsRolesSelector]
                );

                return this.operatorsService.registerWithUser(inputModel).pipe(
                    switchMap((registerResponse: ServerResponseData<OperatorResponseInterface>) => {
                        if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                            // 409: the email already belongs to another user in the tenant (e.g. a client).
                            const errorState = registerResponse.status === HttpStatusCodesEnum.Conflict
                                ? OperatorsFormErrorStatesEnum.UserExists
                                : OperatorsFormErrorStatesEnum.CreateOperatorFailed;

                            return Helper.errorsHelper().createErrorResponseObservable<OperatorResponseInterface>(
                                registerResponse,
                                errorState
                            );
                        }

                        return this.sendOperatorConfirmationEmail(email);
                    })
                );
            })
        );
    }

    private updateOperator(): ServerResponse<boolean> {
        const clonedModel = this.operator.serialize();

        this.operator.firstName = this.form.value[PanelSettingsOperatorsFirstNameSelector];
        this.operator.middleName = this.form.value[PanelSettingsOperatorsMiddleNameSelector];
        this.operator.lastName = this.form.value[PanelSettingsOperatorsLastNameSelector];

        return this.operatorsService.update(this.operator)
            .pipe(
                map((operatorsResponse: ServerResponseData<null>) => {
                    if (operatorsResponse.status !== HttpStatusCodesEnum.Ok) {
                        this.operator = new OperatorModel(clonedModel);

                        return Helper.errorsHelper().createErrorResponse<null>(
                            operatorsResponse,
                            OperatorsFormErrorStatesEnum.UpdateOperatorFailed
                        );
                    }

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    private updateOperatorStatus(trigger: number): ServerResponse<boolean> {
        const statusTriggerModel = new OperatorStatusTriggerModel(
            this.operator.id,
            trigger
        );

        return this.operatorsService.updateStatus(statusTriggerModel).pipe(
            map((updateStatusResponse: ServerResponseData<null>) => {
                if (updateStatusResponse.status !== HttpStatusCodesEnum.Ok) {
                    return Helper.errorsHelper().createErrorResponse(
                        updateStatusResponse,
                        OperatorsFormErrorStatesEnum.UpdateOperatorStatusFailed
                    );
                }

                this.operator.status = Helper.enumsHelper().getOperatorStatusByTrigger(trigger);

                return Helper.createSuccessResponse<boolean>(true);
            })
        );
    }

    private assignRoleToUser(userId: number): ServerResponse<boolean> {
        const userWithRolesModel = new UserWithRolesModel();

        userWithRolesModel.userId = userId;

        (this.form.value[PanelSettingsOperatorsRolesSelector] as number[]).forEach((roleId: number) => {
            const role = new RoleModel();

            role.id = roleId;

            userWithRolesModel.addRole(role);
        });

        return this.usersService.updateRoles(userWithRolesModel)
            .pipe(
                switchMap((response: ServerResponseData<UserWithRolesInterface>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponseObservable<UserWithRolesInterface>(
                            response,
                            OperatorsFormErrorStatesEnum.AssignRoleToUserFailed
                        );
                    }

                    const authUser = this.authApiService.getAuthenticatedUser();

                    if (authUser.id !== userId) {
                        return Helper.createSuccessResponseObservable<boolean>(true);
                    }

                    return this.authApiService.syncAuthenticatedUserRoles(environment.application);
                })
            );
    }

    sendOperatorConfirmationEmail(email: string): ServerResponse<boolean> {
        const sendResetPasswordInputModel = new UserSendResetPasswordInputModel(
            email,
            environment.authId,
            environment.authRedirectUrl
        );

        return this.usersService.sendResetPassword(sendResetPasswordInputModel)
            .pipe(
                map((response: ServerResponseData<null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse<null>(
                            response,
                            OperatorsFormErrorStatesEnum.UserConfirmationEmailSendFailed
                        );
                    }

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    initForm(roles: RoleModel[], statusTriggers: number[] = [], initForEdit: boolean = false): void {
        this.statusTriggers = statusTriggers;

        this.form = new FormGroup({});

        this.controls = PanelSettingsOperatorsFormControls();

        const authUserPermissions = !this.authApiService ? [] : this.authApiService.getAuthenticatedUserPermissions();
        const getRolePermissions = !this.getRolePermissionRule ? [] : this.getRolePermissionRule.permissions;

        if (
            this.getRolePermissionRule &&
            !Helper.getUnionStrategyPermissionsCheck(getRolePermissions, authUserPermissions)
        ) {
            delete this.controls[PanelSettingsOperatorsRolesSelector];
        }

        const controls = Object.keys(this.controls).filter((controlName: string) => {
            if (!initForEdit) {
                return controlName !== PanelSettingsOperatorsStatusSelector;
            }

            return true;
        }).map((key: string) => {
            const control = this.initControl(this.controls[key], roles);

            this.form.addControl(control.name, new FormControl(control.value, control.validators));

            control.floatingLabelNever = initForEdit;

            return this.controls[key];
        });

        this.controlsObservable.next(controls);
    }

    validateForm(
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }
    ): boolean {
        if (this.form.invalid) {
            Object.keys(this.controls).forEach((key: string) => {
                const control = this.controls[key];
                const formControl = this.form.get(control.name);

                if (formControl && formControl.invalid) {
                    const errorKeys = Object.keys(formControl.errors);

                    const errors = errorKeys.map((errorKey: string) => {
                        return messageFunction(control.placeholder, PanelSettingsOperatorsNameMinLength)[errorKey];
                    });

                    control.setErrors(errors);
                }
            });

            return false;
        }

        return true;
    }

    createRequest(): ServerResponse<boolean> {
        if (!this.operator) {
            return this.registerOperator();
        }

        let request: ServerResponse<boolean> = null;

        if (
            this.operator.firstName !== this.form.value[PanelSettingsOperatorsFirstNameSelector] ||
            this.operator.middleName !== this.form.value[PanelSettingsOperatorsMiddleNameSelector] ||
            this.operator.lastName !== this.form.value[PanelSettingsOperatorsLastNameSelector]
        ) {
            request = this.updateOperator();
        }

        const trigger = this.form.value[PanelSettingsOperatorsStatusSelector];

        if (Helper.enumsHelper().getOperatorStatusTriggerByStatus(this.operator.status) !== trigger) {
            if (!request) {
                request = this.updateOperatorStatus(trigger);
            } else {
                request = request.pipe(
                    switchMap((response: ServerResponseData<boolean>) => {
                        if (response.status !== HttpStatusCodesEnum.Ok) {
                            return of(response);
                        }

                        return this.updateOperatorStatus(trigger);
                    })
                );
            }
        }

        const rolesCheck = Helper.compareSimpleArrays<number>(
            (this.form.value[PanelSettingsOperatorsRolesSelector] as number[]),
            (!this.userWithRoles ? [] : this.userWithRoles.getRoleIds())
        );

        if (!rolesCheck) {
            if (!request) {
                return this.assignRoleToUser(this.operator.userId);
            }

            return request.pipe(
                switchMap((response: ServerResponseData<boolean>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return of(response);
                    }

                    return this.assignRoleToUser(this.operator.userId);
                })
            );
        }

        return !request ? Helper.createSuccessResponseObservable<boolean>(true) : request;
    }
}
